'use client';

import { c as _c } from "react/compiler-runtime";
import { AppSearchBar } from '@/components/ui/app-search-bar';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Button, buttonVariants } from '@/components/ui/buttons/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { rememberSearch } from '@/features/payload-cms/components/search/recent-searches';
import type { SearchFilters } from '@/features/payload-cms/components/search/search-filter-panel';
import { filterChipClassName, SearchFilterPanel } from '@/features/payload-cms/components/search/search-filter-panel';
import { SearchPreview } from '@/features/payload-cms/components/search/search-preview';
import { SearchResultRow } from '@/features/payload-cms/components/search/search-result-row';
import { PreviewSkeleton, ResultListSkeleton, SkeletonBar } from '@/features/payload-cms/components/search/search-skeleton';
import { RecentSearches } from '@/features/payload-cms/components/search/search-suggestions';
import { allTypesLabel, alsoFoundLabel, filterLabel, instantErrorLabel, instantOfflineLabel, nextPageLabel, noQueryDescription, noQueryTitle, noResultsDescription, noResultsTitle, noResultsWithFiltersDescription, pageOfLabel, previousPageLabel, resetFiltersLabel, resultCountLabel, retryLabel, searchingLabel, searchPageTitle, searchPlaceholder, showAllLabel, showResultsLabel, typeLabels, typeLabelSingular } from '@/features/payload-cms/components/search/search-translations';
import { useLiveSearch } from '@/features/payload-cms/components/search/use-live-search';
import type { SearchResult, SearchResultType, SiteSearchResponse } from '@/features/payload-cms/search/search-types';
import { SEARCH_RESULT_TYPES } from '@/features/payload-cms/search/search-types';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { ChevronLeft, ChevronRight, Search, SearchX, SlidersHorizontal, WifiOff } from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';
const DEFAULT_FILTERS: SearchFilters = {
  types: [],
  sort: 'relevance',
  period: 'any'
};

/** "4 Seiten, 2 News": what the chosen types leave out, so nothing found goes unseen. */
const AlsoFound: React.FC<{
  counts: Record<SearchResultType, number>;
  chosenTypes: SearchResultType[];
  locale: Locale;
  onShowAll: () => void;
}> = t0 => {
  const $ = _c(31);
  const {
    counts,
    chosenTypes,
    locale,
    onShowAll
  } = t0;
  if (chosenTypes.length === 0) {
    return;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== chosenTypes || $[1] !== counts || $[2] !== locale) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t7;
      if ($[9] !== chosenTypes || $[10] !== counts) {
        t7 = type => !chosenTypes.includes(type) && counts[type] > 0;
        $[9] = chosenTypes;
        $[10] = counts;
        $[11] = t7;
      } else {
        t7 = $[11];
      }
      const others = SEARCH_RESULT_TYPES.filter(t7);
      if (others.length === 0) {
        t6 = undefined;
        break bb0;
      }
      t3 = "bg-quiet mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[14px] px-4 py-2 text-[14px] text-green-700";
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <Search className="h-4 w-4 shrink-0 text-green-500" aria-hidden="true" />;
        $[12] = t4;
      } else {
        t4 = $[12];
      }
      const t8 = alsoFoundLabel[locale];
      if ($[13] !== t8) {
        t5 = <span>{t8}</span>;
        $[13] = t8;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      t1 = "text-heading font-semibold";
      let t9;
      if ($[15] !== counts || $[16] !== locale) {
        t9 = type_0 => `${counts[type_0]} ${(counts[type_0] === 1 ? typeLabelSingular : typeLabels)[type_0][locale]}`;
        $[15] = counts;
        $[16] = locale;
        $[17] = t9;
      } else {
        t9 = $[17];
      }
      t2 = others.map(t9).join(", ");
    }
    $[0] = chosenTypes;
    $[1] = counts;
    $[2] = locale;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[18] !== t1 || $[19] !== t2) {
    t7 = <span className={t1}>{t2}</span>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = showAllLabel[locale];
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChevronRight className="h-4 w-4" aria-hidden="true" />;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== onShowAll || $[23] !== t8) {
    t10 = <button type="button" onClick={onShowAll} className="text-conveniat-green ml-auto flex min-h-11 cursor-pointer items-center gap-0.5 font-semibold">{t8}{t9}</button>;
    $[22] = onShowAll;
    $[23] = t8;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t7) {
    t11 = <div className={t3}>{t4}{t5}{t7}{t10}</div>;
    $[25] = t10;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t7;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
};

/** A count beside a filter, a grey bar while the counts of the current query are on their way. */
const Count: React.FC<{
  value: number | undefined;
}> = t0 => {
  const $ = _c(2);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = value === undefined ? <SkeletonBar className="inline-block h-3 w-3 bg-current opacity-20" /> : <span className="text-[12px] opacity-75">{value}</span>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/** The type chips on a phone, where the panel with the checkboxes sits in a sheet. */
const TypeChips: React.FC<{
  filters: SearchFilters;
  counts: Record<SearchResultType, number> | undefined;
  locale: Locale;
  onChange: (filters: Partial<SearchFilters>) => void;
}> = t0 => {
  const $ = _c(22);
  const {
    filters,
    counts,
    locale,
    onChange
  } = t0;
  const t1 = filters.types.length === 0;
  let t2;
  if ($[0] !== onChange) {
    t2 = () => onChange({
      types: []
    });
    $[0] = onChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = filters.types.length === 0;
  let t4;
  if ($[2] !== t3) {
    t4 = cn(filterChipClassName(t3), "snap-start");
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = allTypesLabel[locale];
  let t6;
  if ($[4] !== counts) {
    t6 = counts === undefined ? undefined : SEARCH_RESULT_TYPES.reduce((sum, type) => sum + counts[type], 0);
    $[4] = counts;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t6) {
    t7 = <Count value={t6} />;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4 || $[11] !== t5 || $[12] !== t7) {
    t8 = <button type="button" aria-pressed={t1} onClick={t2} className={t4}>{t5}{t7}</button>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== counts || $[15] !== filters.types || $[16] !== locale || $[17] !== onChange) {
    t9 = SEARCH_RESULT_TYPES.map(type_0 => {
      const isChosen = filters.types.includes(type_0);
      return <button key={type_0} type="button" aria-pressed={isChosen} disabled={counts?.[type_0] === 0 && !isChosen} onClick={() => onChange({
        types: isChosen ? filters.types.filter(chosen => chosen !== type_0) : [...filters.types, type_0]
      })} className={cn(filterChipClassName(isChosen), "snap-start")}>{typeLabels[type_0][locale]}<Count value={counts?.[type_0]} /></button>;
    });
    $[14] = counts;
    $[15] = filters.types;
    $[16] = locale;
    $[17] = onChange;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6">{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
};
const Pagination: React.FC<{
  page: number;
  totalPages: number;
  locale: Locale;
  onPage: (page: number) => void;
}> = t0 => {
  const $ = _c(18);
  const {
    page,
    totalPages,
    locale,
    onPage
  } = t0;
  if (totalPages <= 1) {
    return;
  }
  let t1;
  if ($[0] !== locale || $[1] !== onPage || $[2] !== page) {
    t1 = page > 1 ? <button type="button" onClick={() => onPage(page - 1)} className={buttonVariants({
      variant: "outlined",
      size: "touch"
    })}><ChevronLeft aria-hidden="true" />{previousPageLabel[locale]}</button> : <span />;
    $[0] = locale;
    $[1] = onPage;
    $[2] = page;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== page || $[5] !== totalPages) {
    t2 = pageOfLabel(page, totalPages);
    $[4] = page;
    $[5] = totalPages;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = t2[locale];
  let t4;
  if ($[7] !== t3) {
    t4 = <span className="text-[14px] text-green-700">{t3}</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== locale || $[10] !== onPage || $[11] !== page || $[12] !== totalPages) {
    t5 = page < totalPages ? <button type="button" onClick={() => onPage(page + 1)} className={buttonVariants({
      variant: "outlined",
      size: "touch"
    })}>{nextPageLabel[locale]}<ChevronRight aria-hidden="true" /></button> : <span />;
    $[9] = locale;
    $[10] = onPage;
    $[11] = page;
    $[12] = totalPages;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t4 || $[16] !== t5) {
    t6 = <nav className="mt-6 flex items-center justify-between gap-3">{t1}{t4}{t5}</nav>;
    $[14] = t1;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};

/**
 * The search page: the query, the filters beside the results on a wide screen or in a sheet on
 * a phone, the results, and from `xl` on a preview of the chosen one. It searches while the
 * visitor types; from the first keystroke until the answer arrives it shows skeletons, never the
 * results of the query before. The filters live in the URL, so a search can be shared.
 */
export const SearchResultsView: React.FC<{
  locale: Locale;
  query: string;
  filters: SearchFilters;
  page: number;
  response: SiteSearchResponse;
}> = t0 => {
  const $ = _c(106);
  const {
    locale,
    query: initialQuery,
    filters: initialFilters,
    page: initialPage,
    response: initialResponse
  } = t0;
  const isDesktop = useIsDesktop();
  let t1;
  if ($[0] !== initialFilters || $[1] !== initialPage || $[2] !== initialQuery || $[3] !== initialResponse) {
    t1 = {
      query: initialQuery,
      filters: initialFilters,
      page: initialPage,
      response: initialResponse
    };
    $[0] = initialFilters;
    $[1] = initialPage;
    $[2] = initialQuery;
    $[3] = initialResponse;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const search = useLiveSearch(locale, t1);
  const {
    draft,
    filters,
    status,
    response,
    words
  } = search;
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState();
  const [isPreviewClosed, setIsPreviewClosed] = useState(false);
  const filterButtonReference = useRef(null);
  let t2;
  if ($[5] !== response?.results) {
    t2 = response?.results ?? [];
    $[5] = response?.results;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const results = t2;
  let t3;
  if ($[7] !== results || $[8] !== selectedKey) {
    t3 = results.find(result => result.key === selectedKey) ?? results.at(0);
    $[7] = results;
    $[8] = selectedKey;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const selected = t3;
  const hasPreviewColumn = !isPreviewClosed;
  const showsPreview = isDesktop && hasPreviewColumn;
  const hasFilters = filters.types.length > 0 || filters.sort !== DEFAULT_FILTERS.sort || filters.period !== DEFAULT_FILTERS.period;
  const sheetFilterCount = (filters.sort === DEFAULT_FILTERS.sort ? 0 : 1) + (filters.period === DEFAULT_FILTERS.period ? 0 : 1);
  let t4;
  if ($[10] !== search) {
    t4 = next => {
      search.setDraft(next);
      rememberSearch(next);
    };
    $[10] = search;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const pickQuery = t4;
  let t5;
  if ($[12] !== draft) {
    t5 = result_0 => {
      setSelectedKey(result_0.key);
      setIsPreviewClosed(false);
      rememberSearch(draft);
    };
    $[12] = draft;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const selectResult = t5;
  let t6;
  if ($[14] !== search) {
    t6 = page => {
      search.setPage(page);
      globalThis.scrollTo({
        top: 0
      });
    };
    $[14] = search;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const changePage = t6;
  const t7 = response?.counts;
  let t8;
  if ($[16] !== filters || $[17] !== locale || $[18] !== search.setFilters || $[19] !== t7) {
    t8 = <SearchFilterPanel filters={filters} counts={t7} locale={locale} onChange={search.setFilters} />;
    $[16] = filters;
    $[17] = locale;
    $[18] = search.setFilters;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const filterPanel = t8;
  let t9;
  if ($[21] !== changePage || $[22] !== draft || $[23] !== filters.types || $[24] !== hasFilters || $[25] !== isDesktop || $[26] !== locale || $[27] !== pickQuery || $[28] !== response || $[29] !== results || $[30] !== search || $[31] !== selectResult || $[32] !== selected?.key || $[33] !== showsPreview || $[34] !== status || $[35] !== words) {
    t9 = () => {
      switch (status) {
        case "idle":
          {
            return <><EmptyState icon={Search} title={noQueryTitle[locale]} description={noQueryDescription[locale]} /><RecentSearches locale={locale} onPick={pickQuery} /></>;
          }
        case "loading":
          {
            return <ResultListSkeleton className="mt-4" />;
          }
        case "offline":
          {
            return <EmptyState icon={WifiOff} title={instantOfflineLabel[locale]} />;
          }
        case "error":
          {
            return <EmptyState icon={SearchX} title={instantErrorLabel[locale]} action={<Button variant="outlined" size="touch" onClick={search.retry}>{retryLabel[locale]}</Button>} />;
          }
        case "ready":
          {
            if (response === undefined) {
              return;
            }
            if (results.length === 0) {
              return <><EmptyState icon={SearchX} title={noResultsTitle[locale]} description={hasFilters ? noResultsWithFiltersDescription[locale] : noResultsDescription[locale]} action={hasFilters ? <Button variant="outlined" size="touch" onClick={() => search.setFilters(DEFAULT_FILTERS)}>{resetFiltersLabel[locale]}</Button> : undefined} /><RecentSearches locale={locale} current={draft} onPick={pickQuery} /></>;
            }
            return <><Card as="ul" divided={true} className="mt-4">{results.map(result_1 => <SearchResultRow key={result_1.key} result={result_1} words={words} locale={locale} isSelected={showsPreview && result_1.key === selected?.key} onSelect={isDesktop ? selectResult : undefined} onOpen={() => rememberSearch(draft)} />)}</Card><AlsoFound counts={response.counts} chosenTypes={filters.types} locale={locale} onShowAll={() => search.setFilters({
                types: []
              })} /><Pagination page={response.page} totalPages={response.totalPages} locale={locale} onPage={changePage} /></>;
          }
      }
    };
    $[21] = changePage;
    $[22] = draft;
    $[23] = filters.types;
    $[24] = hasFilters;
    $[25] = isDesktop;
    $[26] = locale;
    $[27] = pickQuery;
    $[28] = response;
    $[29] = results;
    $[30] = search;
    $[31] = selectResult;
    $[32] = selected?.key;
    $[33] = showsPreview;
    $[34] = status;
    $[35] = words;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  const renderResults = t9;
  let heading = searchPageTitle[locale];
  if (status === "loading") {
    let t10;
    if ($[37] !== draft) {
      t10 = searchingLabel(draft.trim());
      $[37] = draft;
      $[38] = t10;
    } else {
      t10 = $[38];
    }
    heading = t10[locale];
  }
  if (status === "ready" && response !== undefined && (response.total > 0 || hasFilters)) {
    let t10;
    if ($[39] !== draft || $[40] !== response.total) {
      t10 = resultCountLabel(response.total, draft.trim());
      $[39] = draft;
      $[40] = response.total;
      $[41] = t10;
    } else {
      t10 = $[41];
    }
    heading = t10[locale];
  }
  const t10 = hasPreviewColumn && "xl:grid-cols-[220px_minmax(0,1fr)_minmax(400px,460px)] 2xl:grid-cols-[240px_minmax(0,1fr)_540px]";
  let t11;
  if ($[42] !== t10) {
    t11 = cn("lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-10", t10);
    $[42] = t10;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  const t12 = status !== "idle" && filterPanel;
  let t13;
  if ($[44] !== t12) {
    t13 = <aside className="hidden lg:block"><div className="sticky top-24 pt-[76px]">{t12}</div></aside>;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== draft) {
    t14 = event => {
      event.preventDefault();
      rememberSearch(draft);
    };
    $[46] = draft;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  let t16;
  if ($[48] !== search) {
    t15 = event_0 => search.setDraft(event_0.target.value);
    t16 = () => search.setDraft("");
    $[48] = search;
    $[49] = t15;
    $[50] = t16;
  } else {
    t15 = $[49];
    t16 = $[50];
  }
  const t17 = searchPlaceholder[locale];
  const t18 = initialQuery === "";
  let t19;
  if ($[51] !== draft || $[52] !== t15 || $[53] !== t16 || $[54] !== t17 || $[55] !== t18) {
    t19 = <AppSearchBar onPage={true} value={draft} onChange={t15} onClear={t16} placeholder={t17} autoFocus={t18} />;
    $[51] = draft;
    $[52] = t15;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t14 || $[58] !== t19) {
    t20 = <form role="search" onSubmit={t14}>{t19}</form>;
    $[57] = t14;
    $[58] = t19;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== filters || $[61] !== heading || $[62] !== locale || $[63] !== response?.counts || $[64] !== search.setFilters || $[65] !== sheetFilterCount || $[66] !== status) {
    t21 = status !== "idle" && <><div className="mt-5 flex items-center gap-3"><h1 aria-busy={status === "loading"} className="font-heading text-heading min-w-0 flex-1 text-[22px] leading-tight font-extrabold lg:text-[28px]">{heading}</h1><Button ref={filterButtonReference} type="button" variant="quiet" size="touchIcon" aria-label={filterLabel[locale]} onClick={() => setIsFilterSheetOpen(true)} className="relative lg:hidden"><SlidersHorizontal aria-hidden="true" />{sheetFilterCount > 0 && <span className="bg-conveniat-green absolute -top-1 -right-1 rounded-full px-1.5 text-[11px] font-bold text-white">{sheetFilterCount}</span>}</Button></div><div className="mt-3 lg:hidden"><TypeChips filters={filters} counts={response?.counts} locale={locale} onChange={search.setFilters} /></div></>;
    $[60] = filters;
    $[61] = heading;
    $[62] = locale;
    $[63] = response?.counts;
    $[64] = search.setFilters;
    $[65] = sheetFilterCount;
    $[66] = status;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== draft || $[69] !== locale || $[70] !== response || $[71] !== status) {
    t22 = status === "ready" && response !== undefined ? resultCountLabel(response.total, draft.trim())[locale] : "";
    $[68] = draft;
    $[69] = locale;
    $[70] = response;
    $[71] = status;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== t22) {
    t23 = <p className="sr-only" aria-live="polite">{t22}</p>;
    $[73] = t22;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  let t24;
  if ($[75] !== renderResults) {
    t24 = renderResults();
    $[75] = renderResults;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== t20 || $[78] !== t21 || $[79] !== t23 || $[80] !== t24) {
    t25 = <main className="min-w-0">{t20}{t21}{t23}{t24}</main>;
    $[77] = t20;
    $[78] = t21;
    $[79] = t23;
    $[80] = t24;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  let t26;
  if ($[82] !== hasPreviewColumn || $[83] !== locale || $[84] !== selected || $[85] !== status || $[86] !== words) {
    t26 = hasPreviewColumn && <aside className="hidden xl:block">{status === "loading" && <Card className="sticky top-24 mt-[76px] p-6"><PreviewSkeleton /></Card>}{status === "ready" && selected !== undefined && <Card className="sticky top-24 mt-[76px] max-h-[calc(100dvh-7rem)] overflow-y-auto p-6"><SearchPreview key={selected.key} result={selected} words={words} locale={locale} onClose={() => setIsPreviewClosed(true)} /></Card>}</aside>;
    $[82] = hasPreviewColumn;
    $[83] = locale;
    $[84] = selected;
    $[85] = status;
    $[86] = words;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  let t27;
  if ($[88] !== t11 || $[89] !== t13 || $[90] !== t25 || $[91] !== t26) {
    t27 = <div className={t11}>{t13}{t25}{t26}</div>;
    $[88] = t11;
    $[89] = t13;
    $[90] = t25;
    $[91] = t26;
    $[92] = t27;
  } else {
    t27 = $[92];
  }
  const t28 = filterLabel[locale];
  let t29;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsFilterSheetOpen(false);
    $[93] = t29;
  } else {
    t29 = $[93];
  }
  const t30 = response?.total ?? 0;
  let t31;
  if ($[94] !== t30) {
    t31 = showResultsLabel(t30);
    $[94] = t30;
    $[95] = t31;
  } else {
    t31 = $[95];
  }
  const t32 = t31[locale];
  let t33;
  if ($[96] !== t32) {
    t33 = <Button variant="primary" size="touch" className="w-full" onClick={t29}>{t32}</Button>;
    $[96] = t32;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  let t34;
  if ($[98] !== filterPanel || $[99] !== isFilterSheetOpen || $[100] !== t28 || $[101] !== t33) {
    t34 = <BottomSheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen} title={t28} anchor={filterButtonReference} footer={t33}>{filterPanel}</BottomSheet>;
    $[98] = filterPanel;
    $[99] = isFilterSheetOpen;
    $[100] = t28;
    $[101] = t33;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== t27 || $[104] !== t34) {
    t35 = <div className="mx-auto w-full max-w-[1680px] px-4 pt-6 pb-16 sm:px-6 xl:px-10">{t27}{t34}</div>;
    $[103] = t27;
    $[104] = t34;
    $[105] = t35;
  } else {
    t35 = $[105];
  }
  return t35;
};